<template>
  <page :title="title" :breadcrumb="breadcrumb">
    <v-card>
      <div>
        <v-card-title>
          <div class="headline">Currency Details</div>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <message :messages="messages"></message>
          <v-container fluid grid-list-lg>
            <v-layout row wrap>
              <v-flex xs6 v-if="newCurr">
                <v-text-field :label="labels.curr" v-model="currForm.curr" :error-messages="errors.collect('curr')" data-vv-name="curr" :data-vv-as="labels.curr" v-validate="'required|param|max:4'"></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{labels.curr}}</v-subheader>
                <div class="title">{{currForm.curr}}</div>
              </v-flex>
              <v-flex xs6>
                <v-text-field :label="labels.currDesc" v-model="currForm.currDesc" :error-messages="errors.collect('currDesc')" data-vv-name="currDesc" :data-vv-as="labels.currDesc" v-validate="'required|entry|max:40'"></v-text-field>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-btn color="primary" dark class="mb-2" @click="doNewCurr" v-if="newCurr">
          Add Currency
          <v-icon right>mdi-plus</v-icon>
        </v-btn>
        <v-btn color="primary" dark class="mb-2" @click="doUpdateCurr" v-else>
          Update Currency
          <v-icon right>mdi-content-save</v-icon>
        </v-btn>
      </div>
      <v-divider></v-divider>
      <div v-if="!newCurr">
        <v-card-title>
          <div class="headline">Currency Rate Listing</div>
          <v-spacer></v-spacer>
          <v-text-field append-icon="mdi-magnify" label="Search" single-line hide-details v-model="search"></v-text-field>
        </v-card-title>
        <v-data-table :headers="headers" :items="items" :search="search" :rows-per-page-items="itemsPerPage" :pagination.sync="pagination" item-key="name" :loading="loading" :no-data-text="noDataText" :filter="customSearch">
          <template slot="headerCell" slot-scope="props">
            <v-tooltip bottom>
              <span slot="activator">
                {{ props.header.text }}
              </span>
              <span>
                {{ props.header.text }}
              </span>
            </v-tooltip>
          </template>
          <template slot="items" slot-scope="props">
            <td class="text-xs-left">{{ props.item.effDate }}</td>
            <td class="text-xs-left">{{ props.item.rate }}</td>
            <td class="text-xs-left">{{ props.item.expDate }}</td>
            <td class="text-xs-right">
              <table-action :rowData="props" review @review="doReviewRate(curr, props.item.effDate)" remove @remove="doPromptRemove(props.item.id)"></table-action>
            </td>
          </template>
        </v-data-table>
        <v-btn color="primary" dark class="mb-2" @click="doNewRate">
          Add Currency Rate
          <v-icon right>mdi-plus</v-icon>
        </v-btn>
        <v-dialog v-model="currRateDialog" width="800px" lazy persistent>
          <v-card>
            <v-card-title>
              <span class="headline">Currency Rate</span>
            </v-card-title>
            <v-card-text>
              <message :messages="messages"></message>
              <v-container grid-list-md>
                <v-layout wrap>
                  <v-flex xs6 v-if="newCurrRate">
                    <cal-picker :label="labels.effDate" v-model="currRateForm.effDate" :error-messages="errors.collect('effDate')" data-vv-name="effDate" :data-vv-as="labels.effDate" v-validate="'required'"></cal-picker>
                  </v-flex>
                  <v-flex xs6 v-else>
                    <v-subheader class="queryLabel">{{labels.effDate}}</v-subheader>
                    <div class="title">{{formatDate(currRateForm.effDate)}}</div>
                  </v-flex>
                  <v-flex xs6>
                    <v-text-field :label="labels.rate" v-model="currRateForm.rate" :error-messages="errors.collect('rate')" data-vv-name="rate" :data-vv-as="labels.rate" v-validate="'required|checkDec|max:18'"></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout>
                  <v-flex xs6>
                    <cal-picker :label="labels.expDate" v-model.number="currRateForm.expDate" :error-messages="errors.collect('expDate')" data-vv-name="expDate" :data-vv-as="labels.expDate"></cal-picker>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="red darken-1" flat @click.native="doClose">Cancel</v-btn>
              <v-btn color="primary" flat @click.native="doSave">Save</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </v-card>
    <confirm v-model="promptRemove" @ok="doDelete">
      <p>Are you sure you want to delete the selected item?</p>
      <div class="title"><p>Currency: {{selectedId.curr}}</p><p>Effective Date: {{formatDisplayDate(selectedId.effDate)}}</p></div>
    </confirm>
  </page>
</template>

<script>
import { mapGetters } from 'vuex'
import * as TableConst from '@/mixins/table'
import MessageMixin from '@/mixins/message'
import { expandURI } from '@/utils/ajax'
import { formatDisplayDate } from '@/utils/date'

export default {
  data() {
    return {
      breadcrumb: [],
      title: 'Manage Currency Rate',
      labels: {
        curr: 'Currency*',
        currDesc: 'Description*',
        rate: 'Rate*',
        effDate: 'Effective Date*',
        expDate: 'Expire Date'
      },
      currForm: {
        curr: null,
        currDesc: null
      },
      headers: [
        { text: 'Effective Date', value: 'effDate' },
        { text: 'Rate', value: 'rate' },
        { text: 'Expire Date', value: 'expDate' },
        TableConst.tableActionHeader
      ],
      currRateForm: {
        curr: this.$route.params.curr,
        rate: null,
        effDate: null,
        expDate: null
      },
      currRateDialog: false,
      newCurrRate: false,
      newCurr: true,
      selectedId: {
        curr: null,
        effDate: null,
        rate: 0,
        expDate: null
      },
      promptRemove: false
    }
  },
  methods: {
    async doFetchGeneral() {
      const apiURI = this.currMenu.getCurrency
      if (apiURI) {
        const expand = {
          curr: this.curr
        }
        const result = await this.$http.get(expandURI(apiURI, expand))
        const { data } = result.data
        if (result.data) {
          this.currForm.curr = data.currency.curr
          this.currForm.currDesc = data.currency.currDesc
        }
      }
    },
    async doFetchCurrRate(options) {
      this.loading = true
      this.selectedId.curr = null
      this.selectedId.effDate = null
      const apiURI = this.currMenu.getAllCurrencyRate
      if (apiURI) {
        try {
          const expand = {
            curr: this.curr
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          const { data } = result.data
          this.items = []
          if (data) {
            data.map((currRate) => {
              const convertList = {
                id: {
                  curr: currRate.curr,
                  effDate: currRate.effDate
                },
                rate: currRate.rate,
                effDate: formatDisplayDate(currRate.effDate),
                expDate: formatDisplayDate(currRate.expDate)
              }
              this.items.push(convertList)
            })
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.fullMessage(data)
          }
        }
      } else {
        this.errorMessage('Invalid API URL')
      }
      this.loading = false
    },
    async doNewCurr() {
      this.resetMessages()
      const result = await this.$validator.validateAll()
      if (result) {
        const apiURI = this.currMenu.createCurrency
        if (apiURI) {
          try {
            const result = await this.$http.post(apiURI, this.currForm)
            const { data } = result
            if (data) {
              this.notify(data)
              const redirect = {
                name: 'currList'
              }
              this.$router.push(redirect)
            }
          } catch (err) {
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          }
        }
      } else {
        this.promptFieldCheck()
      }
    },
    async doUpdateCurr() {
      this.resetMessages()
      const result = await this.$validator.validate('currDesc')
      if (result) {
        const apiURI = this.currMenu.updateCurrency
        if (apiURI) {
          try {
            const result = await this.$http.put(apiURI, this.currForm)
            const { data } = result
            if (data) {
              this.notify(data)
              this.doFetchGeneral()
            }
          } catch (err) {
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          }
        }
      } else {
        this.promptFieldCheck()
      }
    },
    async doReviewRate(curr, effDate) {
      const apiURI = this.currMenu.getCurrencyRate
      if (apiURI) {
        const expand = {
          curr,
          effDate
        }
        const result = await this.$http.get(expandURI(apiURI, expand))
        const { data } = result.data
        if (result.data) {
          this.currRateForm.rate = data.currRate.rate
          this.currRateForm.effDate = data.currRate.effDate
          this.currRateForm.expDate = data.currRate.expDate
          this.currRateDialog = true
        }
      }
    },
    doNewRate() {
      this.currRateDialog = true
      this.newCurrRate = true
      this.initForm()
      const now = new Date().setHours(0, 0, 0, 0)
      this.currRateForm.effDate = new Date(now)
    },
    doSave() {
      this.resetMessages()
      this.$validator.validateAll().then(result => {
        if (result) {
          if (this.newCurrRate) {
            this.doCreateRate()
          } else {
            this.doUpdateRate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doCreateRate() {
      const apiURI = this.currMenu.createCurrencyRate
      if (apiURI) {
        try {
          const result = await this.$http.post(apiURI, this.currRateForm)
          const { data } = result
          if (data) {
            this.notify(data)
            this.doFetchCurrRate(false)
            this.doClose()
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doUpdateRate() {
      const apiURI = this.currMenu.updateCurrencyRate
      if (apiURI) {
        try {
          const result = await this.$http.put(apiURI, this.currRateForm)
          const { data } = result
          if (data) {
            this.notify(data)
            this.doFetchCurrRate(false)
            this.doClose()
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    doPromptRemove(currRate) {
      this.resetMessages()
      this.selectedId.curr = currRate.curr
      this.selectedId.effDate = currRate.effDate
      this.promptRemove = true
    },
    doDelete() {
      const apiURI = this.currMenu.deleteCurrencyRate
      if (apiURI) {
        this.$http.post(apiURI, this.selectedId).then(result => {
          const data = result.data
          this.notify(data)
          this.doFetchCurrRate()
        }).catch(err => {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        })
      }
    },
    doClose() {
      this.currRateDialog = false
      this.newCurrRate = false
      this.initForm()
    },
    initForm() {
      this.resetValidator()
      this.resetMessages()
      this.currRateForm.effDate = null
      this.currRateForm.expDate = null
      this.currRateForm.rate = null
    },
    formatDisplayDate(xDate) {
      return formatDisplayDate(xDate)
    }
  },
  created() {
    this.breadcrumb = this.derieveBreadcrumbByName('currList')
    this.breadcrumb.push({
      name: 'currReview',
      text: this.title
    })
    const checkDec = {
      getMessage: (field) => {
        return 'The ' + field + ' field must be a valid decimal.'
      },
      validate: (value) => {
        return !Number.isNaN(Number(value))
      }
    }
    this.$validator.extend('checkDec', checkDec)
    if (this.curr === 'new') {
      this.newCurr = true
    } else {
      this.newCurr = false
      this.doFetchGeneral()
      this.doFetchCurrRate(true)
    }
  },
  computed: {
    ...mapGetters([
      'derieveBreadcrumbByName',
      'checkAccess'
    ]),
    currMenu() {
      return this.$store.state.currency.menu
    },
    curr() {
      return this.$route.params.curr
    }
  },
  mixins: [
    TableConst.default,
    MessageMixin
  ]
}
</script>

<style>
</style>
